import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Input, Label } from '@clarix/ui';
import { acceptInviteSignupSchema } from '@clarix/utils';
import { PasswordInput } from '../auth/PasswordInput';
import { PasswordErrorMessage } from '../auth/PasswordErrorMessage';
import {
  useAcceptInvite,
  useAcceptAndSignup,
  useInvitePreview,
  useMe,
  normalizeError,
} from '@/core/api';
import { buildWorkspaceUrl } from '@/gateway/getWorkspaceSlug';

/**
 * Public invite landing page at /invites/:token.
 *
 * Three branches based on the preview response + current auth state, gated on
 * `needsPassword = requires_signup || !has_password` (a brand-new email OR an
 * existing OTP-only account that has no password yet):
 *
 *   A) needsPassword === true
 *      → Set-password form (no method toggle) → POST /invites/:token/accept-and-signup
 *        creates the account (or sets a password on the existing passwordless
 *        one) + membership and returns a JWT bound to the workspace. We hand that
 *        token to the workspace subdomain via the hash handoff so the member
 *        lands signed-in; on later visits they sign in with the password they
 *        just set or an emailed code.
 *
 *   B) needsPassword === false AND user is signed in AND email matches
 *      → Existing password account. Accept the invite, then redirect to the
 *        workspace subdomain's login.
 *
 *   C) needsPassword === false AND (not signed in OR wrong email)
 *      → Redirect to the workspace subdomain's login (email prefilled).
 */

type FieldErrors = Partial<Record<'name' | 'password' | 'confirmPassword', string>>;

export function InvitePreviewPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const preview = useInvitePreview(token ?? null);
  const me = useMe();

  const acceptAndSignup = useAcceptAndSignup(token ?? null);
  const accept = useAcceptInvite();

  // New-account form state (only used when requires_signup === true)
  const [form, setForm] = useState({ name: '', password: '', confirmPassword: '' });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const clearFieldError = (field: keyof FieldErrors) =>
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));

  // Send the member to the workspace subdomain's login, email prefilled. Used
  // by the existing-account branches, where the user signs in on the subdomain.
  const goToWorkspaceLogin = (slug: string, email: string) => {
    window.location.assign(buildWorkspaceUrl(slug, `/login?email=${encodeURIComponent(email)}`));
  };

  // Hand the freshly-minted JWT to the workspace subdomain via the hash handoff
  // so the new member lands signed-in (same mechanism as LoginPage.completeLogin).
  const goToWorkspaceAuthed = (slug: string, accessToken: string) => {
    const target = buildWorkspaceUrl(slug, '/');
    window.location.assign(`${target}#token=${encodeURIComponent(accessToken)}`);
  };

  const invitedEmail = preview.data?.email ?? '';
  const myEmail = (me.data?.user?.email ?? '').toLowerCase();
  const isSignedInAsInvited = !!me.data?.user && myEmail === invitedEmail.toLowerCase();

  // New invitee: set a name + password, which creates the account + membership
  // and returns a JWT. Hand that token to the workspace subdomain so they land
  // signed-in; future logins accept either the password or an emailed code.
  const handleSignupAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !preview.data) return;

    // Setting a password is mandatory for a new invitee: validate name +
    // password + confirm, create the account, and hand the returned JWT to the
    // workspace subdomain (lands signed-in).
    const parsed = acceptInviteSignupSchema.safeParse(form);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FieldErrors;
        if (key && !next[key]) next[key] = issue.message;
      }
      setFieldErrors(next);
      return;
    }
    setFieldErrors({});
    try {
      const result = await acceptAndSignup.mutateAsync({
        name: parsed.data.name,
        password: parsed.data.password,
      });
      goToWorkspaceAuthed(result.workspace.slug, result.access_token);
    } catch {
      /* error rendered inline */
    }
  };

  // Existing account: accept the invite (creates membership), then send them to
  // the workspace subdomain's OTP login.
  const handleAcceptExisting = async () => {
    if (!token || !preview.data) return;
    try {
      await accept.mutateAsync(token);
      goToWorkspaceLogin(preview.data.workspace.slug, invitedEmail);
    } catch {
      /* error rendered inline */
    }
  };

  if (preview.isLoading) {
    return (
      <Card>
        <p className="text-xl text-slate-500">Loading invite…</p>
      </Card>
    );
  }
  if (preview.isError || !preview.data) {
    return (
      <Card>
        <h1 className="text-4xl font-semibold text-slate-900 dark:text-slate-100">
          Invite link broken
        </h1>
        <p className="mt-2 text-xl text-slate-500">
          {preview.error ? normalizeError(preview.error).message : 'Unknown error.'}
        </p>
        <p className="mt-3 text-lg text-slate-400">
          Ask the workspace owner to send a fresh invite.
        </p>
      </Card>
    );
  }

  const { workspace, inviter, requires_signup, has_password, workspace_role, apps, expires_at } =
    preview.data;
  // Show the set-password form for a brand-new email (requires_signup) AND for an
  // existing OTP-only account that has no password yet (!has_password) — both
  // need to set one. An account that already has a password skips straight to
  // sign-in (Branch B/C).
  const needsPassword = requires_signup || !has_password;
  // Plain computation — NOT a hook. A useMemo here ran after the loading/error
  // early returns above, so it was conditional and broke the Rules of Hooks
  // ("Rendered more hooks than during the previous render"). It's cheap enough
  // to recompute every render.
  const expiresOn = new Date(expires_at).toLocaleDateString();

  return (
    <Card>
      <header className="text-center">
        <h1 className="text-4xl font-semibold text-slate-900 dark:text-slate-100">
          Join {workspace.name}
        </h1>
        <p className="mt-1 text-lg text-slate-500">
          {inviter ? `${inviter.name} invited` : 'You were invited'}{' '}
          <span className="font-medium text-slate-700 dark:text-slate-300">{invitedEmail}</span> as{' '}
          <span className="font-medium uppercase">{workspace_role}</span>
          {apps?.length ? (
            <>
              {' '}
              with access to{' '}
              <span className="font-medium">{apps.map((a) => a.code).join(', ')}</span>
            </>
          ) : null}
          .
        </p>
        <p className="mt-1 text-lg text-slate-400">Expires {expiresOn}.</p>
      </header>

      {/* Branch A — new email OR existing passwordless account: set a name +
          password, then land signed-in */}
      {needsPassword && (
        <form onSubmit={handleSignupAccept} className="mt-6 space-y-4" noValidate>
          <div>
            <Label htmlFor="invite-name">Your name</Label>
            <Input
              id="invite-name"
              type="text"
              autoComplete="name"
              autoFocus
              value={form.name}
              aria-invalid={!!fieldErrors.name || undefined}
              onChange={(e) => {
                setForm((f) => ({ ...f, name: e.target.value }));
                clearFieldError('name');
              }}
              placeholder="Jane Cooper"
              required
            />
            {fieldErrors.name && (
              <p className="mt-1 text-lg text-red-600 dark:text-red-400">{fieldErrors.name}</p>
            )}
          </div>

          <div>
            <Label htmlFor="invite-password">Password</Label>
            <PasswordInput
              id="invite-password"
              autoComplete="new-password"
              minLength={8}
              value={form.password}
              aria-invalid={!!fieldErrors.password || undefined}
              onChange={(e) => {
                setForm((f) => ({ ...f, password: e.target.value }));
                clearFieldError('password');
              }}
              placeholder="At least 8 characters"
              required
            />
            {fieldErrors.password && (
              <p className="mt-1 text-lg text-red-600 dark:text-red-400">{fieldErrors.password}</p>
            )}
          </div>

          <div>
            <Label htmlFor="invite-confirm-password">Confirm password</Label>
            <PasswordInput
              id="invite-confirm-password"
              autoComplete="new-password"
              minLength={8}
              value={form.confirmPassword}
              aria-invalid={!!fieldErrors.confirmPassword || undefined}
              onChange={(e) => {
                setForm((f) => ({ ...f, confirmPassword: e.target.value }));
                clearFieldError('confirmPassword');
              }}
              placeholder="Re-enter your password"
              required
            />
            {fieldErrors.confirmPassword && (
              <p className="mt-1 text-lg text-red-600 dark:text-red-400">
                {fieldErrors.confirmPassword}
              </p>
            )}
          </div>

          {acceptAndSignup.error && (
            <div
              className="rounded-md bg-red-50 px-3 py-2 text-lg text-red-700 dark:bg-red-950/40 dark:text-red-300"
              role="alert"
            >
              <PasswordErrorMessage error={acceptAndSignup.error} />
            </div>
          )}

          <Button type="submit" className="w-full" disabled={acceptAndSignup.isPending}>
            {acceptAndSignup.isPending ? 'Joining…' : `Join ${workspace.name}`}
          </Button>
        </form>
      )}

      {/* Branch B — signed in as the invited email */}
      {!needsPassword && isSignedInAsInvited && (
        <div className="mt-6 space-y-3">
          {accept.error && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-lg text-red-700 dark:bg-red-950/40 dark:text-red-300">
              {normalizeError(accept.error).message}
            </p>
          )}
          <Button
            type="button"
            onClick={handleAcceptExisting}
            disabled={accept.isPending}
            className="w-full"
          >
            {accept.isPending ? 'Joining…' : `Accept and continue to ${workspace.name}`}
          </Button>
          <p className="text-center text-lg text-slate-400">
            Signed in as {myEmail}.{' '}
            <button
              type="button"
              className="text-indigo-600 hover:underline dark:text-indigo-400"
              onClick={() => navigate(`/login?next=${encodeURIComponent(`/invites/${token}`)}`)}
            >
              Wrong account?
            </button>
          </p>
        </div>
      )}

      {/* Branch C — existing password account but not signed in (or signed in as someone else) */}
      {!needsPassword && !isSignedInAsInvited && (
        <div className="mt-6 space-y-3">
          <Button
            type="button"
            className="w-full"
            onClick={() => goToWorkspaceLogin(workspace.slug, invitedEmail)}
          >
            Continue — sign in as {invitedEmail}
          </Button>
          {me.data?.user && (
            <p className="text-center text-lg text-amber-700 dark:text-amber-300">
              You're currently signed in as {myEmail}. The invite is for {invitedEmail} — sign in
              with that email to accept.
            </p>
          )}
          <p className="text-center text-lg text-slate-400">
            We'll bring you back here automatically after sign-in.
          </p>
        </div>
      )}
    </Card>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-md rounded-2xl bg-white p-8 shadow-sm ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
      {children}
    </div>
  );
}
